import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_SECTION_MARGINS, SectionMargins, TwoColumnsWysiwygContent } from "api/shop";
import { CourseGroupAttributeType, useCourseGroupAttributesAll } from "api/coursegroup-attributes";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";
import { HeadingTagSelect, PaziaFormButton, PaziaInput, PaziaSelect } from "components/atoms";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { MdAdd, MdDelete } from "react-icons/md";
import * as yup from "yup";
import { ANIMATION_OPTIONS } from "./animationOptions";
import { ButtonVariantSelect } from "./ButtonVariantSelect";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";

interface TwoColumnsWysiwygFormProps {
  onSubmit: (data: TwoColumnsWysiwygFormValues) => void;
  onCancel: () => void;
  defaultValues?: TwoColumnsWysiwygContent;
  isLoading?: boolean;
}

export const TwoColumnsWysiwygForm = ({ onSubmit, onCancel, defaultValues, isLoading }: TwoColumnsWysiwygFormProps) => {
  const { allAttributes } = useCourseGroupAttributesAll();
  const wysiwygAttributes = allAttributes.filter((a) => a.attributeType === CourseGroupAttributeType.Wysiwyg);
  const attributeOptions = wysiwygAttributes.map((a) => ({ value: a.id, label: a.name }));

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.ANIMATION]: defaultValues?.animation ?? "fade-bottom",
      [FORM_NAMES.HEADER_TEXT]: defaultValues?.header?.text ?? "",
      [FORM_NAMES.HEADER_ALIGN]: defaultValues?.header?.align ?? "",
      [FORM_NAMES.HEADER_SIZE]: defaultValues?.header?.size ?? "",
      [FORM_NAMES.HEADER_TAG]: defaultValues?.header?.tag ?? "h2",
      [FORM_NAMES.LEFT_COLUMN_DATA]: defaultValues?.leftColumn?.data ?? "",
      [FORM_NAMES.RIGHT_COLUMN_DATA]: defaultValues?.rightColumn?.data ?? "",
      [FORM_NAMES.LEFT_COLUMN_SOURCE_MODE]: defaultValues?.leftColumnAttributeId != null ? "attribute" : "manual",
      [FORM_NAMES.RIGHT_COLUMN_SOURCE_MODE]: defaultValues?.rightColumnAttributeId != null ? "attribute" : "manual",
      [FORM_NAMES.LEFT_COLUMN_ATTRIBUTE_ID]: defaultValues?.leftColumnAttributeId ?? null,
      [FORM_NAMES.RIGHT_COLUMN_ATTRIBUTE_ID]: defaultValues?.rightColumnAttributeId ?? null,
      [FORM_NAMES.BUTTONS]: defaultValues?.buttons ?? [],
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit, control, setValue } = methods;

  const leftColumnSourceMode = useWatch({ control, name: FORM_NAMES.LEFT_COLUMN_SOURCE_MODE });
  const rightColumnSourceMode = useWatch({ control, name: FORM_NAMES.RIGHT_COLUMN_SOURCE_MODE });

  const { fields, append, remove } = useFieldArray({
    control,
    name: FORM_NAMES.BUTTONS,
  });

  const handleFormSubmit = (data: FormValues) => {
    const formattedData: TwoColumnsWysiwygFormValues = {
      animation: data.animation,
      header: {
        text: data.headerText,
        align: (data.headerAlign || undefined) as any,
        size: (data.headerSize || undefined) as any,
        tag: data.headerTag as any,
      },
      leftColumn: { data: data.leftColumnSourceMode === "attribute" ? "" : data.leftColumnData },
      rightColumn: { data: data.rightColumnSourceMode === "attribute" ? "" : data.rightColumnData },
      leftColumnAttributeId:
        data.leftColumnSourceMode === "attribute" && data.leftColumnAttributeId != null
          ? data.leftColumnAttributeId
          : undefined,
      rightColumnAttributeId:
        data.rightColumnSourceMode === "attribute" && data.rightColumnAttributeId != null
          ? data.rightColumnAttributeId
          : undefined,
      buttons: data.buttons.map((b) => ({
        label: b.label,
        variant: b.variant as "primary" | "secondary" | "outline",
        href: b.href,
      })),
      margins: data.margins,
    };
    onSubmit(formattedData);
  };

  const addButton = () => {
    append({ label: "", variant: "primary", href: "" });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <div className="grid gap-3 md:grid-cols-2">
          <PaziaSelect
            name={FORM_NAMES.ANIMATION}
            label="Animacja"
            required
            options={ANIMATION_OPTIONS}
          />
          <PaziaInput name={FORM_NAMES.HEADER_TEXT} label="Nagłówek" />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <PaziaSelect
            name={FORM_NAMES.HEADER_ALIGN}
            label="Wyrównanie nagłówka"
            options={HEADER_ALIGN_OPTIONS}
          />
          <PaziaSelect
            name={FORM_NAMES.HEADER_SIZE}
            label="Rozmiar nagłówka"
            options={HEADER_SIZE_OPTIONS}
          />
        </div>

        <HeadingTagSelect name={FORM_NAMES.HEADER_TAG} />

        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Źródło lewej kolumny</span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setValue(FORM_NAMES.LEFT_COLUMN_SOURCE_MODE, "manual")}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    leftColumnSourceMode === "manual"
                      ? "bg-indigo-600 text-white"
                      : "border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                  }`}
                >
                  Treść ręczna
                </button>
                <button
                  type="button"
                  onClick={() => setValue(FORM_NAMES.LEFT_COLUMN_SOURCE_MODE, "attribute")}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    leftColumnSourceMode === "attribute"
                      ? "bg-indigo-600 text-white"
                      : "border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                  }`}
                >
                  Atrybut jako źródło
                </button>
              </div>
            </div>

            {leftColumnSourceMode === "manual" ? (
              <PaziaFromQuill name={FORM_NAMES.LEFT_COLUMN_DATA} label="Lewa kolumna" />
            ) : (
              <>
                {wysiwygAttributes.length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">Brak atrybutów WYSIWYG</p>
                ) : (
                  <PaziaSelect
                    name={FORM_NAMES.LEFT_COLUMN_ATTRIBUTE_ID}
                    label="Atrybut WYSIWYG (lewa kolumna)"
                    options={attributeOptions}
                  />
                )}
              </>
            )}
          </div>

          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Źródło prawej kolumny</span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setValue(FORM_NAMES.RIGHT_COLUMN_SOURCE_MODE, "manual")}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    rightColumnSourceMode === "manual"
                      ? "bg-indigo-600 text-white"
                      : "border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                  }`}
                >
                  Treść ręczna
                </button>
                <button
                  type="button"
                  onClick={() => setValue(FORM_NAMES.RIGHT_COLUMN_SOURCE_MODE, "attribute")}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    rightColumnSourceMode === "attribute"
                      ? "bg-indigo-600 text-white"
                      : "border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                  }`}
                >
                  Atrybut jako źródło
                </button>
              </div>
            </div>

            {rightColumnSourceMode === "manual" ? (
              <PaziaFromQuill name={FORM_NAMES.RIGHT_COLUMN_DATA} label="Prawa kolumna" />
            ) : (
              <>
                {wysiwygAttributes.length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">Brak atrybutów WYSIWYG</p>
                ) : (
                  <PaziaSelect
                    name={FORM_NAMES.RIGHT_COLUMN_ATTRIBUTE_ID}
                    label="Atrybut WYSIWYG (prawa kolumna)"
                    options={attributeOptions}
                  />
                )}
              </>
            )}
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Przyciski</h4>
            <button
              type="button"
              onClick={addButton}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj przycisk
            </button>
          </div>

          {fields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak przycisków. Kliknij "Dodaj przycisk", aby dodać.
            </p>
          ) : (
            <div className="space-y-3">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-800"
                >
                  <div className="grid flex-1 gap-3 md:grid-cols-3">
                    <PaziaInput
                      name={`${FORM_NAMES.BUTTONS}.${index}.label`}
                      label="Tekst"
                      required
                    />
                    <ButtonVariantSelect
                      name={`${FORM_NAMES.BUTTONS}.${index}.variant`}
                      required
                    />
                    <PaziaInput
                      name={`${FORM_NAMES.BUTTONS}.${index}.href`}
                      label="Link (href)"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="mt-6 rounded p-2 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                    title="Usuń przycisk"
                  >
                    <MdDelete size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

const FORM_NAMES = {
  ANIMATION: "animation",
  HEADER_TEXT: "headerText",
  HEADER_ALIGN: "headerAlign",
  HEADER_SIZE: "headerSize",
  HEADER_TAG: "headerTag",
  LEFT_COLUMN_DATA: "leftColumnData",
  RIGHT_COLUMN_DATA: "rightColumnData",
  LEFT_COLUMN_SOURCE_MODE: "leftColumnSourceMode",
  RIGHT_COLUMN_SOURCE_MODE: "rightColumnSourceMode",
  LEFT_COLUMN_ATTRIBUTE_ID: "leftColumnAttributeId",
  RIGHT_COLUMN_ATTRIBUTE_ID: "rightColumnAttributeId",
  BUTTONS: "buttons",
} as const;

const HEADER_ALIGN_OPTIONS = [
  { value: "", label: "Domyślny (z globalnych ustawień)" },
  { value: "left", label: "Do lewej" },
  { value: "center", label: "Wyśrodkowany" },
  { value: "right", label: "Do prawej" },
];

const HEADER_SIZE_OPTIONS = [
  { value: "", label: "Domyślny (z globalnych ustawień)" },
  { value: "sm", label: "Mały" },
  { value: "md", label: "Średni" },
  { value: "lg", label: "Duży" },
  { value: "xl", label: "Bardzo duży" },
];

const buttonSchema = yup.object({
  label: yup.string().required("Tekst przycisku jest wymagany"),
  variant: yup.string().required("Wariant jest wymagany").default("primary"),
  href: yup.string().required("Link jest wymagany"),
});

const schema = yup
  .object({
    [FORM_NAMES.ANIMATION]: yup.string().required("Animacja jest wymagana"),
    [FORM_NAMES.HEADER_TEXT]: yup.string(),
    [FORM_NAMES.HEADER_ALIGN]: yup.string(),
    [FORM_NAMES.HEADER_SIZE]: yup.string(),
    [FORM_NAMES.HEADER_TAG]: yup.string(),
    [FORM_NAMES.LEFT_COLUMN_DATA]: yup.string(),
    [FORM_NAMES.RIGHT_COLUMN_DATA]: yup.string(),
    [FORM_NAMES.LEFT_COLUMN_SOURCE_MODE]: yup.string().required(),
    [FORM_NAMES.RIGHT_COLUMN_SOURCE_MODE]: yup.string().required(),
    [FORM_NAMES.LEFT_COLUMN_ATTRIBUTE_ID]: yup.number().nullable(),
    [FORM_NAMES.RIGHT_COLUMN_ATTRIBUTE_ID]: yup.number().nullable(),
    [FORM_NAMES.BUTTONS]: yup.array().of(buttonSchema).default([]),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

interface FormValues {
  animation: string;
  headerText: string;
  headerAlign: string;
  headerSize: string;
  headerTag: string;
  leftColumnData: string;
  rightColumnData: string;
  leftColumnSourceMode: string;
  rightColumnSourceMode: string;
  leftColumnAttributeId: number | null;
  rightColumnAttributeId: number | null;
  buttons: Array<{ label: string; variant: string; href: string }>;
  margins: SectionMargins;
}

export interface TwoColumnsWysiwygFormValues {
  animation: string;
  header: { text: string; align?: "left" | "center" | "right"; size?: "sm" | "md" | "lg" | "xl"; tag?: string };
  leftColumn: { data: string };
  rightColumn: { data: string };
  leftColumnAttributeId?: number;
  rightColumnAttributeId?: number;
  buttons: Array<{
    label: string;
    variant: "primary" | "secondary" | "outline";
    href: string;
  }>;
  margins?: SectionMargins;
}
